import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import dayjs from 'dayjs'
import { createUseStyles, useTheme } from 'react-jss'
import cn from 'classnames'
import PropTypes from 'prop-types'

import { Button, List, Modal, Tooltip } from 'antd'
import {
  InfoCircleOutlined,
  LeftOutlined,
  LinkOutlined,
  NotificationOutlined,
  RightOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { useAsync, useIsMobile } from '../../hooks'
import api from '../../api'

/**
Returns the day portion of the provided date string.
@param {string} date - The date string.
@returns {string} - The day portion of the date string.
*/
const getDateDay = date => dayjs(date).format('YYYY-MM-DD')

/**
 * Filters an array of entries based on the provided date field and returns the entries grouped by day of the month.
 *
 * @param {Array} arr - The array of entries.
 * @param {Date} monthDate - The month date to filter the entries.
 * @param {string} [dateField = 'created_at'] - The field in the entries to filter by.
 * @returns {Array} - An array of entries grouped by day of the month.
 */
const getMonthEntriesByDay = (arr, monthDate, dateField = 'createdAt') => {
  try {
    // Filter this month entries by provided field and sort them.
    const sorted = _.sortBy(
      arr.filter(entry => entry?.[dateField]?.startsWith(monthDate) || false),
      [dateField],
    )

    // Group entries by day.
    const entriesPerDay = sorted.reduce((sum, entry) => {
      const date = getDateDay(entry[dateField])
      const index = sum.findIndex(item => item.date === date)
      if (index > -1) {
        sum[index].entries.push(entry)
      } else sum.push({ date, entries: [entry] })
      return sum
    }, [])

    return entriesPerDay
  } catch (e) {
    return []
  }
}

/**
Returns the month portion of the provided date string.
@param {string} date - The date string.
@returns {string} - The month portion of the date string.
*/
const getDateMonth = date => dayjs(date).format('YYYY-MM')

/**
 * Returns the translate percent to position the date inside the month cell.
 * @param {*} date - The date string
 * @returns - The percentage as a string.
 */
const getTranslatePosition = date => {
  const day = dayjs(date).date()
  const daysInMonth = dayjs(date).daysInMonth()
  return `${Math.floor((day * 100) / daysInMonth)}%`
}

/**
 * Returns an array with the body value of an async call, or an empty array.
 * @param {*} asyncResponse - The result of an useAsync function.
 * @returns - An array with the body or an empty array.
 */
const getAsyncBody = asyncValue => asyncValue?.body || []

const NOTIF_TYPE_CONFIG = {
  connectivity: {
    icon: <NotificationOutlined />,
    label: 'Connectivity',
  },
  info_request: {
    icon: <InfoCircleOutlined />,
    label: 'Info Request',
  },
  deactivation: {
    icon: <StopOutlined />,
    label: 'Deactivation',
  },
}

const getNotifTypeConfig = type =>
  NOTIF_TYPE_CONFIG[type] ?? {
    icon: <NotificationOutlined />,
    label: type ?? 'Notification',
  }

const START_DATE = dayjs()
  .subtract(12, 'months')
  .startOf('month')
  .format('YYYY-MM-DD')
const RANGE = 14
const END_DATE = dayjs(START_DATE)
  .add(RANGE - 1, 'months')
  .endOf('month')
  .format('YYYY-MM-DD')
const TD_WIDTH = 200
// The label column is pinned and every month column matches it, so at 200px a
// phone shows the labels plus half of a single month. Both shrink together.
const MOBILE_TD_WIDTH = 112
const ROW_HEIGHT = 60
// Narrower labels wrap onto two lines, and the first row stacks three of them,
// which no longer fits 60px. Vertical room is the cheap axis on a phone. The
// label column is absolutely positioned 8.5px into the header, so the row also
// has to be tall enough that centring the labels clears that overlap.
const MOBILE_ROW_HEIGHT = 108

function EmptyCell({ css }) {
  return (
    <td
      className={cn([css.tdTitleCell, css.tdTitleCellEmpty])}
      aria-label="Empty Cell"
    >
      <div className={cn([css.titleCell, css.tdHeadYearDiv])} />
    </td>
  )
}

EmptyCell.propTypes = {
  css: PropTypes.shape().isRequired,
}

const Timeline = forwardRef(({ practiceId, patientId }, ref) => {
  const {
    brandPrimary,
    brandSuccess,
    blue,
    grayPrimary,
    brandWarning,
    brandDangerMid,
    mustardYellow,
  } = useTheme()
  const isMobile = useIsMobile()
  const tdWidth = isMobile ? MOBILE_TD_WIDTH : TD_WIDTH
  const tdWidthPx = `${tdWidth}px`
  const rowHeightPx = `${isMobile ? MOBILE_ROW_HEIGHT : ROW_HEIGHT}px`
  const grayBorder = {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EDEDED',
  }
  const css = createUseStyles({
    container: {
      overflowX: 'scroll',
      paddingBottom: '10px',
      scrollbarWidth: 'thin',
      scrollbarColor: `${grayPrimary} transparent`,
      '&::-webkit-scrollbar': {
        '-webkit-appearance': 'none',
        width: 8,
        height: 8,
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 4,
        backgroundColor: grayPrimary,
        '-webkit-box-shadow': '0 0 1px rgba(255,255,255,.5)',
      },
      scrollBehavior: 'smooth',
    },
    table: {
      borderCollapse: 'collapse',
      marginLeft: 'var(--tl-td-width)',
      '& tr:last-child td': {
        borderBottomWidth: '1px',
      },
    },
    tdGrayBorder: {
      ...grayBorder,
    },
    td: {
      padding: '10px 14px',
      ...grayBorder,
    },
    tdContent: {
      borderBottomWidth: '0',
      borderTopWidth: '0',
      padding: '0',
      height: 'var(--tl-row-height)',
    },
    tdHeadMonth: {
      fontWeight: 'bold',
      '&:nth-child(1)': {
        borderLeftWidth: 0,
      },
    },
    tdHeadYear: {
      fontWeight: 'bold',
      color: brandPrimary,
      border: 'none',
      padding: '0',
      '&:last-child div': {
        borderRadius: '0 8px 0 0',
        borderRightWidth: '1px',
      },
    },
    tdHeadYearDiv: {
      ...grayBorder,
      borderLeftWidth: 0,
      borderBottom: 'none',
      padding: '12px 14px',
    },
    stickyYear: {
      position: 'sticky',
      left: 'calc(var(--tl-td-width) + 10px)',
    },
    tbody: {
      backgroundColor: '#F7F7F8',
    },
    tdWrapper: {
      width: 'var(--tl-td-width)',
      position: 'relative',
      height: '100%',
      padding: '0 10px',
    },
    dayEntry: {
      borderRadius: '4px',
      transformOrigin: 'center',
      padding: '4px 20px',
      color: 'white',
      fontSize: '1em',
      display: 'inline-block',
      position: 'absolute',
      borderWidth: '1px',
      borderColor: '#F7F7F8',
      borderStyle: 'solid',
      transition: 'font-weight .2s ease',
      '--antd-wave-shadow-color': 'none',
      '&::selection': {
        background: 'none',
      },
      '&:hover': {
        color: 'white',
        zIndex: '32  !important',
      },
    },
    alertDayColor: {
      backgroundColor: brandDangerMid,
    },
    officeCheckColor: {
      backgroundColor: brandSuccess,
    },
    receivedDayColor: {
      backgroundColor: blue,
    },
    missedTransmissionDayColor: {
      backgroundColor: 'white',
      borderColor: brandWarning,
      color: brandWarning,
      '&:hover, &:active': {
        color: brandWarning,
      },
    },
    missedAuthorizationDayColor: {
      backgroundColor: brandWarning,
    },
    notificationDayColor: {
      backgroundColor: mustardYellow,
    },
    titleCellNotifications: {
      color: mustardYellow,
      borderBottomWidth: 1,
      '&::before': {
        background: mustardYellow,
      },
    },
    rowLabelNotifications: {
      '&::before': {
        backgroundColor: mustardYellow,
      },
      color: mustardYellow,
    },
    tdTitleCell: {
      background: 'none',
      padding: 0,
      position: 'absolute',
      left: 0,
      zIndex: 100,
    },
    tdTitleCellEmpty: {
      background: 'white',
      '& div': {
        borderWidth: '0 1px 0 0',
        width: 'calc(var(--tl-td-width) + 1px)',
      },
      '& + td, & + td div': {
        borderLeftWidth: 0,
      },
    },
    titleCell: {
      background: 'white',
      borderRadius: '4px 0 0 4px',
      height: 'var(--tl-row-height)',
      display: 'flex',
      alignContent: 'center',
      fontWeight: 'bold',
      padding: '0 15px',
      fontSize: '.8em',
      flexWrap: 'wrap',
      position: 'relative',
      overflow: 'hidden',
      width: 'var(--tl-td-width)',
      borderRightWidth: 0,
      borderBottomWidth: 0,
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '5px',
        height: '100%',
        display: 'block',
        top: '0',
        left: '0',
      },
      ...grayBorder,
    },
    titleCellOfficeChecks: {
      color: brandSuccess,
      borderBottomWidth: 1,
      '&::before': {
        background: brandSuccess,
      },
    },
    titleCellAlerts: {
      color: brandDangerMid,
      '&::before': {
        background: brandDangerMid,
      },
    },
    titleCellTransmissions: {
      color: blue,
      '&::before': {
        background: blue,
        height: '50%',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        width: '5px',
        height: '100%',
        display: 'block',
        top: '50%',
        left: '0',
        background: brandWarning,
      },
    },
    tdCurrentMonth: {
      background: '#ebe8e8',
    },
    rowLabel: {
      '&::before': {
        content: '""',
        width: 15,
        height: 10,
        display: 'inline-block',
        marginRight: 5,
        borderRadius: 3,
      },
      marginBottom: 2,
    },
    rowLabelReceived: {
      '&::before': {
        backgroundColor: blue,
      },
      color: blue,
    },
    rowLabelNoAuthorization: {
      '&::before': {
        backgroundColor: brandWarning,
      },
      color: brandWarning,
    },
    rowLabelNoTransmission: {
      '&::before': {
        border: `solid 1px ${brandWarning}`,
      },
      color: brandWarning,
    },
    rowLabelAlerts: {
      '&::before': {
        backgroundColor: brandDangerMid,
      },
    },
    rowLabelOfficeChecks: {
      '&::before': {
        backgroundColor: brandSuccess,
      },
    },
    scrollButton: {
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      borderColor: brandPrimary,
      color: brandPrimary,
      '&:first-child': {
        marginRight: '10px',
      },
    },
    tableHeadCorner: {
      width: 'calc(var(--tl-td-width) + 1px)',
      height: '88px',
      background: 'white',
      display: 'flex',
      borderRight: 'solid 1px #EDEDED',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      flexDirection: 'column',
      padding: '5px 10px 10px 10px',
    },
  })()

  // Define the columns for the table
  const monthDates = _.range(RANGE)
    .map(i => {
      const date = dayjs(START_DATE)
        .add(i, 'months')
        .startOf('month')
        .format('YYYY-MM-DD')
      return {
        date,
        year: dayjs(date).format('YYYY'),
      }
    })
    .reduce((sum, item, i, dates) => {
      // Check if it's the first year appearance
      const find = dates.findIndex(d => d.year === item.year)

      return [
        ...sum,
        {
          ...item,
          // In that case, look for the last appearance and get how many cells the year spans
          span:
            find === i
              ? dates.findLastIndex(d => d.year === item.year) - find + 1
              : 0,
        },
      ]
    }, [])

  // Fetch all dates for the timeline.
  const getReports = useCallback(
    async () =>
      api.patients.getReports(patientId, null, 100, {
        start_date: START_DATE,
        end_date: END_DATE,
      }),
    [patientId],
  )
  const getMissedTransmissions = useCallback(
    async () => api.patients.getMissedTransmissions(patientId, 100),
    [patientId],
  )
  const getAlerts = useCallback(
    async () => api.patients.getAlerts(patientId, { per_page: 100, page: 1 }),
    [patientId],
  )
  const getOfficeChecks = useCallback(
    async () =>
      api.patients.getOfficeChecks(practiceId, patientId, {
        from_date: START_DATE,
        to_date: dayjs().format('YYYY-MM-DD'),
        per_page: 100,
        page: 1,
      }),
    [patientId, practiceId],
  )
  const getNotifications = useCallback(
    async () =>
      api.notifications.list({
        patient_id: patientId,
        per_page: 100,
        page: 1,
      }),
    [patientId],
  )

  const missedTransmissions = useAsync(getMissedTransmissions)
  const receivedTransmissions = useAsync(getReports)
  const alerts = useAsync(getAlerts)
  const officeChecks = useAsync(getOfficeChecks)
  const notifications = useAsync(getNotifications)

  useImperativeHandle(ref, () => ({
    refreshNotifications: () => notifications.execute(),
  }), [notifications])

  // Map all entries to it's month.
  const mappedMonthEntries = useMemo(() => {
    const reports = getAsyncBody(receivedTransmissions.value) || []
    const missed = getAsyncBody(missedTransmissions.value) || []
    const transmissions = {
      received: reports.filter(report => report.authorized),
      missedAuthorizations: reports.filter(report => !report.authorized),
      missedTransmissions: [],
    }

    // Every entry in missed transmissions that doesn't have a matching
    // report is a missed transmission.
    transmissions.missedTransmissions = missed.filter(
      m => !reports.some(report => report.id === m?.reportId),
    )

    return monthDates.map(month => ({
      ...month,
      alerts: getMonthEntriesByDay(
        getAsyncBody(alerts.value),
        getDateMonth(month.date),
      ),
      received: getMonthEntriesByDay(
        transmissions.received,
        getDateMonth(month.date),
        'reportDate',
      ),
      missedTransmissions: getMonthEntriesByDay(
        transmissions.missedTransmissions,
        getDateMonth(month.date),
        'cycleEndedAt',
      ),
      missedAuthorizations: getMonthEntriesByDay(
        transmissions.missedAuthorizations,
        getDateMonth(month.date),
        'reportDate',
      ),
      officeChecks: getMonthEntriesByDay(
        getAsyncBody(officeChecks.value),
        getDateMonth(month.date),
        'checkDate',
      ),
      notifications: getMonthEntriesByDay(
        getAsyncBody(notifications.value),
        getDateMonth(month.date),
      ),
    }))
  }, [
    alerts.value,
    missedTransmissions.value,
    monthDates,
    notifications.value,
    officeChecks.value,
    receivedTransmissions.value,
  ])

  /**
   * Renders a td for every month and passes the month entry to a rendering prop.
   */
  const MonthColumns = useCallback(
    ({ renderer }) =>
      mappedMonthEntries.map(monthEntries => (
        <td
          key={monthEntries.date}
          className={cn([css.td, css.tdContent], {
            [css.tdCurrentMonth]:
              monthEntries.date === `${dayjs().format('YYYY-MM')}-01`,
          })}
        >
          <div className={css.tdWrapper}>{renderer(monthEntries)}</div>
        </td>
      )),
    [
      css.td,
      css.tdContent,
      css.tdCurrentMonth,
      css.tdWrapper,
      mappedMonthEntries,
    ],
  )

  /**
   * Renders all date entries in a month as spans relatively positioned.
   */
  const renderMonthEntries = ({
    entries,
    className,
    link,
    tooltip,
    onMultiple,
  }) => {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (entries.length === 0) return <></>

    return entries.map(dayEntry => {
      const formattedDay = dayjs(dayEntry.date).format('DD')
      const position = getTranslatePosition(dayEntry.date)
      const isMulti =
        typeof onMultiple === 'function' && dayEntry.entries.length > 1

      // We can use a function to build the link or use a link property in the entry.
      let url
      try {
        if (isMulti) url = '#'
        else if (typeof link === 'function') url = link(dayEntry)
        else if (typeof dayEntry?.link === 'string') url = dayEntry.link
        else url = '#'
      } catch {
        url = '#'
      }
      const anchor = (
        <a
          href={url}
          title={formattedDay}
          key={dayEntry.date}
          target="_blank"
          rel="noreferrer"
          className={cn([css.dayEntry, className])}
          style={{
            left: position,
            top: '50%',
            transform: `translate(-${position}, -50%)`,
            zIndex: dayjs(dayEntry.date).format('D'),
          }}
          onClick={
            isMulti
              ? e => {
                  e.preventDefault()
                  onMultiple(dayEntry)
                }
              : undefined
          }
        >
          {formattedDay}
        </a>
      )
      if (typeof tooltip === 'function') {
        return (
          <Tooltip key={dayEntry.date} title={tooltip(dayEntry)}>
            {anchor}
          </Tooltip>
        )
      }
      return anchor
    })
  }

  const tableRef = useRef(null)

  const maxScrollLeft = tdWidth * 12 + 12

  const [scrollLeft, setScrollLeft] = useState(0)
  const [multiNotifDay, setMultiNotifDay] = useState(null)

  useEffect(() => {
    if (tableRef.current) setScrollLeft(maxScrollLeft)
  }, [maxScrollLeft, patientId])

  useEffect(() => {
    if (tableRef.current) tableRef.current.scrollLeft = scrollLeft
  }, [scrollLeft])

  const getLastEntry = entry => entry.entries[entry.entries.length - 1]

  const handleScrollLeft = () => {
    setScrollLeft(
      Math.max(scrollLeft - (tableRef.current.clientWidth - tdWidth), 0),
    )
  }

  const handleScrollRight = () => {
    setScrollLeft(
      Math.min(
        maxScrollLeft,
        scrollLeft + tableRef.current.clientWidth + tdWidth,
      ),
    )
  }

  return (
    // The column width has to travel as a custom property: createUseStyles runs
    // inside this component, so JSS builds the sheet on the first render — when
    // antd's breakpoints are still unresolved and useIsMobile reports desktop —
    // and never rebuilds it. A variable is read at paint instead.
    <div
      style={{
        position: 'relative',
        '--tl-td-width': tdWidthPx,
        '--tl-row-height': rowHeightPx,
      }}
    >
      <div className={cn(['Timeline', css.container])} ref={tableRef}>
        <table className={css.table}>
          <thead>
            <tr className={css.trHeadYear}>
              <td
                className={cn([css.tdTitleCell])}
                style={{ zIndex: 102 }}
                rowSpan={2}
              >
                <div className={css.tableHeadCorner}>
                  <div>
                    <Button
                      onClick={handleScrollLeft}
                      className={css.scrollButton}
                      disabled={scrollLeft === 0}
                    >
                      <LeftOutlined />
                    </Button>
                    <Button
                      onClick={handleScrollRight}
                      className={css.scrollButton}
                      disabled={scrollLeft === maxScrollLeft}
                    >
                      <RightOutlined />
                    </Button>
                  </div>
                  <span>
                    {dayjs(monthDates[0].date).format('MMM YYYY')} -{' '}
                    {dayjs(monthDates[monthDates.length - 1].date).format(
                      'MMM YYYY',
                    )}
                  </span>
                </div>
              </td>
              {monthDates
                // Only year cells
                .filter(m => m.span)
                .map(date => (
                  <td
                    key={date.year}
                    className={cn([css.td, css.tdHeadYear])}
                    colSpan={date.span}
                  >
                    <div className={css.tdHeadYearDiv}>
                      <span className={css.stickyYear}>{date.year}</span>
                    </div>
                  </td>
                ))}
            </tr>
            <tr>
              <EmptyCell css={css} />
              {monthDates.map(date => (
                <td
                  key={date.date}
                  className={cn([css.td, css.tdHeadMonth], {
                    [css.tdCurrentMonth]:
                      date.date === `${dayjs().format('YYYY-MM')}-01`,
                  })}
                >
                  {dayjs(date.date).format('MMMM')}
                </td>
              ))}
            </tr>
          </thead>
          <tbody className={css.tbody}>
            <tr>
              <td className={cn([css.tdTitleCell])}>
                <div
                  className={cn([css.titleCell, css.titleCellTransmissions])}
                >
                  <div className={cn(css.rowLabel, css.rowLabelReceived)}>
                    BioBridge Reports
                  </div>
                  <div
                    className={cn(css.rowLabel, css.rowLabelNoAuthorization)}
                  >
                    Missing Authorization
                  </div>
                  <div className={cn(css.rowLabel, css.rowLabelNoTransmission)}>
                    Missing Transmission
                  </div>
                </div>
              </td>
              <MonthColumns
                renderer={monthEntry => (
                  <div className={css.tdReceived}>
                    {renderMonthEntries({
                      entries: monthEntry.received,
                      className: css.receivedDayColor,
                      link: entry => {
                        const lastEntry = getLastEntry(entry)
                        const basePath =
                          lastEntry.resource === 'Report'
                            ? 'reports'
                            : 'remote-checks'
                        return `/${basePath}/${lastEntry?.id}`
                      },
                    })}
                    {renderMonthEntries({
                      entries: monthEntry.missedAuthorizations,
                      className: css.missedAuthorizationDayColor,
                      link: entry => {
                        const lastEntry = getLastEntry(entry)
                        const basePath =
                          lastEntry.resource === 'Report'
                            ? 'reports'
                            : 'remote-checks'
                        return `/${basePath}/${lastEntry?.id}`
                      },
                    })}
                    {renderMonthEntries({
                      entries: monthEntry.missedTransmissions,
                      className: css.missedTransmissionDayColor,
                      link: entry =>
                        `/missed-transmissions/${getLastEntry(entry)?.id}`,
                    })}
                  </div>
                )}
              />
            </tr>
            <tr>
              <td className={cn([css.tdTitleCell])}>
                <div className={cn([css.titleCell, css.titleCellAlerts])}>
                  <div className={cn(css.rowLabel, css.rowLabelAlerts)}>
                    Alerts
                  </div>
                </div>
              </td>
              <MonthColumns
                renderer={monthEntry =>
                  renderMonthEntries({
                    entries: monthEntry.alerts,
                    className: css.alertDayColor,
                    link: entry => `/alerts/${getLastEntry(entry)?.id}`,
                  })
                }
              />
            </tr>
            <tr>
              <td className={cn([css.tdTitleCell])}>
                <div className={cn([css.titleCell, css.titleCellOfficeChecks])}>
                  <div className={cn(css.rowLabel, css.rowLabelOfficeChecks)}>
                    Office Checks
                  </div>
                </div>
              </td>
              <MonthColumns
                renderer={monthEntry =>
                  renderMonthEntries({
                    entries: monthEntry.officeChecks,
                    className: css.officeCheckColor,
                    link: entry =>
                      `/practices/${practiceId}/office-checks/${
                        getLastEntry(entry)?.id
                      }`,
                  })
                }
              />
            </tr>
            <tr>
              <td className={cn([css.tdTitleCell])}>
                <div
                  className={cn([
                    css.titleCell,
                    css.titleCellNotifications,
                  ])}
                >
                  <div
                    className={cn(
                      css.rowLabel,
                      css.rowLabelNotifications,
                    )}
                  >
                    Notifications
                  </div>
                </div>
              </td>
              <MonthColumns
                renderer={monthEntry =>
                  renderMonthEntries({
                    entries: monthEntry.notifications,
                    className: css.notificationDayColor,
                    link: entry =>
                      `/notifications/${getLastEntry(entry)?.id}`,
                    tooltip: dayEntry =>
                      dayEntry.entries.map(e => e.title).join(', '),
                    onMultiple: dayEntry =>
                      setMultiNotifDay(dayEntry.entries),
                  })
                }
              />
            </tr>
          </tbody>
        </table>
      </div>
      <Modal
        title="Notifications"
        open={multiNotifDay !== null}
        onCancel={() => setMultiNotifDay(null)}
        footer={null}
        centered
      >
        <List
          itemLayout="horizontal"
          dataSource={multiNotifDay ?? []}
          renderItem={entry => {
            const { icon, label } = getNotifTypeConfig(entry.notificationType)
            const formattedDate = dayjs(entry.createdAt).format('MMM D, YYYY')
            return (
              <List.Item style={{ padding: '12px 0' }}>
                <a
                  href={`/notifications/${entry.id}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'block', width: '100%', color: 'inherit' }}
                  onClick={() => setMultiNotifDay(null)}
                >
                  <List.Item.Meta
                    avatar={
                      <span
                        style={{
                          fontSize: 20,
                          color: '#6b6b6b',
                          lineHeight: '40px',
                        }}
                      >
                        {icon}
                      </span>
                    }
                    title={
                      <span
                        style={{
                          fontWeight: 500,
                          color: '#262626',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        {entry.title}
                        <LinkOutlined
                          style={{ fontSize: 12, color: '#6b6b6b' }}
                        />
                      </span>
                    }
                    description={
                      <span style={{ color: '#6b6b6b', fontSize: 12 }}>
                        {label} · {formattedDate}
                        {entry.author?.firstName && (
                          <> · {_.startCase([entry.author.firstName, entry.author.lastName].filter(Boolean).join(' '))}</>
                        )}
                      </span>
                    }
                  />
                </a>
              </List.Item>
            )
          }}
        />
      </Modal>
    </div>
  )
})

Timeline.propTypes = {
  practiceId: PropTypes.string.isRequired,
  patientId: PropTypes.string.isRequired,
}


export default Timeline
